import { CrossCircledIcon } from "@radix-ui/react-icons";
import cx from "classnames";
import Fuse from "fuse.js";
import React, { useState, useEffect, useMemo } from "react";
import ReactModal from "react-modal";
import { Cross, Search } from "../../../../components/common/Icons";
import { Colours } from "../../../../utils/colours";
import Label from "../../Label/Label";
import { NDSTextInput } from "../../TextInput/TextInput";
import * as styles from "./SelectLookup.module.scss";

interface SelectLookupProps {
	id: string;
	// The name for the select
	name: string;
	// The label for the select
	label: string;
	// The options for the select
	options: NDS.FormOption[];
	// The options which always show regardless of the search
	persistentOptions?: NDS.FormOption[];
	// The value of the selected option
	value: string;
	// The function to call when an option is selected
	onChange: (value: string) => void;
	onUpdateSearchValue?: (value: string) => void;
	// The error message to display
	errorMessage?: string;
	// Whether the select is hidden
	hidden: boolean;

	// Whether to use the label as a placeholder
	labelAsPlaceholder?: boolean;

	// Placeholder text for the select
	placeholder?: string;

	disabled?: boolean;
	threshold?: number;
}
export const SelectLookup: React.FC<SelectLookupProps> = ({
	id,
	name,
	value,
	label,
	options,
	onChange,
	disabled,
	persistentOptions = [],
	threshold = 0.7,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const [searchValue, setSearchValue] = useState("");

	const results = useMemo(() => {
		if (!searchValue) return options;
		const fuse = new Fuse(options, {
			keys: ["label"],
			threshold,
		});

		return [...fuse.search(searchValue).map((result) => result.item)].filter(
			(item, index, self) =>
				index === self.findIndex((t) => t.label === item.label),
		);
	}, [searchValue, options, threshold]);

	const requestClose = () => {
		setIsOpen(false);
	};

	const updateSelectedItem = (value: string) => {
		onChange(value);
		setIsOpen(false);
	};

	useEffect(() => {
		ReactModal.setAppElement("body");
	}, []);

	return (
		<div className={styles.selectlookupwrapper}>
			<Label name={name} label={label} id={id} />
			<div className={styles.selectlookup}>
				<button
					className={cx({
						[styles.selectlookuptrigger]: value?.length > 0,
						[styles.selectlookuptriggerempty]: !(value?.length > 0),
					})}
					type="button"
					onClick={() => setIsOpen(!isOpen)}
					disabled={disabled}
				>
					<div className={styles.selectlookuptriggertext}>
						{/* We need to display a user friendly value (e.g. Prefer not to say instead of 'prefer_not_to_say') */}
						{/* Bug first discovered on Q28_1 - Ticket: DT-4257 */}
						{value
							? persistentOptions.find((option) => option.value === value)
									?.label ||
								(value.length > 20 ? value.slice(0, 20).concat("...") : value)
							: "Search"}
					</div>
					<Search fill={Colours.MIDNIGHT} height={"24px"} />
				</button>
				{value?.length > 0 && (
					<button
						className={styles.selectlookupclear}
						type="button"
						onClick={() => {
							updateSelectedItem("");
							onChange(null);
						}}
						aria-label="Clear selection"
					>
						<CrossCircledIcon height={"28px"} width={"28px"} />
					</button>
				)}
			</div>
			<ReactModal
				isOpen={isOpen}
				preventScroll
				shouldCloseOnOverlayClick
				onRequestClose={requestClose}
				shouldReturnFocusAfterClose
				contentElement={() => (
					// biome-ignore lint/a11y/useKeyWithClickEvents: Required to stop the modal closing when clicking on the overlay
					<div
						className={styles.selectlookupmodal}
						onClick={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							id={"close-address-picker"}
							className={styles.selectlookupclosebutton}
							onClick={requestClose}
							aria-label="Close popup"
							// biome-ignore lint/a11y/noAutofocus: Legit use case
							autoFocus={true}
							onKeyDown={(e: { key: string }) => {
								if (e.key === "Escape") {
									requestClose();
								}
							}}
						>
							<Cross fill={Colours.MIDNIGHT} height={"16px"} />
						</button>
						<h3>{label}</h3>
						<NDSTextInput
							className={styles.selectlookupsearchinput}
							id={`${name}-searchinput`}
							name={`${name}-searchinput`}
							label={"Start typing to search"}
							placeholder={"Start typing to search"}
							value={searchValue}
							onChange={(value) => setSearchValue(value)}
							onKeyDown={(e: { key: string }) => {
								if (e.key === "Escape") {
									requestClose();
								}
							}}
						/>
						<div className={styles.selectlookuplist}>
							{[...results, ...persistentOptions].map((result, i) => (
								<button
									type="button"
									key={result.label}
									id={result.label}
									tabIndex={0}
									className={styles.selectlookupitem}
									onClick={() => updateSelectedItem(result.value)}
									onKeyDown={(e) => {
										// Focus trapping inside the modal - go back to the close button if you tab past the last address
										if (i === results.length - 1 && e.key === "Tab") {
											e.preventDefault();
											document.getElementById("close-address-picker")?.focus();
										}
										if (e.key === "Escape") {
											requestClose();
										}
									}}
								>
									{result.label}
								</button>
							))}
						</div>
					</div>
				)}
				style={{
					overlay: {
						position: "fixed",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						paddingTop: "100px",
						height: "100%",
						width: "100%",
						padding: 0,
						margin: 0,
						zIndex: 999,
						boxSizing: "border-box",
						backgroundColor: "rgba(0, 0, 0, 0.5)",
					},
					content: {
						position: "relative",
						height: "100%",
						width: "100%",
						backgroundColor: "transparent",
						border: 0,
						margin: 0,
						marginTop: "100px",
						padding: 0,
						paddingTop: "25%",
						display: "flex",
						justifyContent: "center",
					},
				}}
			/>
		</div>
	);
};
